import BetaCourseCompletionModal from "./BetaCourseCompletionModal";
import CourseCompletionModal from "./CourseCompletionModal";
import CourseStatusLabel from "@components/CourseStatusLabel";
import LanguageSwitcher, { useLanguageSwitcher } from "./LanguageSwitcher";
import Prompt from "../Prompt";
import React, {
  ComponentProps,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import UnsavedProgress from "../UnsavedProgress";
import { Blank, FillInTheBlanks } from "../FillInTheBlanks";
import { Blockquote } from "../Blockquote";
import {
  Box,
  Button,
  ButtonGroup,
  ButtonProps,
  ChakraProps,
  Divider,
  Flex,
  Heading,
  IconButton,
  Link,
  LinkProps,
  ListItem,
  ListItemProps,
  ListProps,
  OrderedList,
  Stack,
  StackProps,
  Text,
  Tooltip,
  UnorderedList,
  chakra,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
  usePrevious,
} from "@chakra-ui/react";
import { Capsule } from "@components/Capsule";
import {
  Cloudinary,
  CloudinaryGif,
  RoundCornerImage,
  Screenshot,
} from "../Screenshot";
import {
  CodeBlock,
  EmbeddableExplorer,
  LineNumbersContext,
  MarkdownCodeBlock,
  MultiCodeBlock,
  MultiCodeBlockContext,
} from "@apollo/chakra-helpers";

import "prismjs/components/prism-bash";
import "prismjs/components/prism-csharp";
import "prismjs/components/prism-diff";
import "prismjs/components/prism-graphql";
import "prismjs/components/prism-groovy";
import "prismjs/components/prism-java";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-json";
import "prismjs/components/prism-jsx";
import "prismjs/components/prism-kotlin";
import "prismjs/components/prism-python";
import "prismjs/components/prism-swift";
import "prismjs/components/prism-tsx";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-yaml";

import { CodeChallenge } from "../CodeChallenge";
import { CollapsibleSection } from "../CollapsibleSection";
import { ConditionalText } from "../ConditionalText";
import { ConfirmVersion } from "./ConfirmVersion";
import { ConnectorsModal } from "./ConnectorsModal";
import { CourseNav } from "../CourseNav";
import { CustomLink } from "../CustomLink";
import { DeployOnRailwayButton } from "../DeployOnRailwayButton";
import { Disclaimer } from "../Disclaimer";
import { EmbedRatio, PortalContext } from "../EmbedRatio";
import { FeedbackLesson } from "../FeedbackLesson";
import {
  FiChevronsLeft,
  FiChevronsRight,
  FiClock,
  FiDownload,
  FiLifeBuoy,
} from "react-icons/fi";
import { Link as GatsbyLink, withPrefix } from "gatsby";
import { GatsbySeo } from "gatsby-plugin-next-seo";
import { GetUserDocument, GetUserQuery, useSaveCourseMutation } from "@graphql";
import { Header } from "../Header";
import { HeadingProps } from "react-markdown/lib/ast-to-react";
import { Helmet } from "react-helmet-async";
import { HighlightApollopediaTerms } from "@components/apollopedia/HighlightApollopediaTerms";
import { InlineCode } from "../InlineCode";
import { InteractiveConnectorsPlayground } from "@components/InteractiveConnectorsPlayground";
import { LessonPagination } from "./LessonPagination";
import { LessonTextContent } from "../LessonTextContent";
import { LogoTitle } from "../LogoTitle";
import { MDXProvider } from "@mdx-js/react";
import { MDXRenderer } from "gatsby-plugin-mdx";
import { MultipleChoice } from "../MultipleChoice";
import { OutPortal, createHtmlPortalNode } from "react-reverse-portal";
import { PageSeo } from "@components/PageSeo";
import { PrivateBlock } from "../PrivateBlock";
import { Task, TaskContext, TaskGroup } from "../Task";
import { TroubleshootingSection } from "../TroubleshootingSection";
import { VersionBanner } from "./VersionBanner";
import { VideoDataProps, WistiaEmbed } from "../WistiaEmbed";
import { ViewCodeCollapsible } from "../ViewCodeCollapsible";
import {
  filterLessonsByLanguage,
  queryTasks,
  sendReport,
  useUser,
} from "@utils";
import { outdent } from "outdent";
import { useLocalStorage, useUpdateEffect } from "react-use";
import { useMDXScope } from "gatsby-plugin-mdx/context";
import { useMarketo } from "@utils/marketo";

const EXPANDED_SIDEBAR_WIDTH = 360;
const COLLAPSED_SIDEBAR_WIDTH = 60;

function Table(props: ChakraProps) {
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const bgColor = useColorModeValue("blackAlpha.50", "whiteAlpha.50");
  return (
    <chakra.table
      borderWidth="1px"
      sx={{
        "th, td": {
          borderColor,
          borderWidth: 1,
          py: 2,
          px: 3,
        },
        "tr:nth-of-type(2n) td": { bgColor },
      }}
      {...props}
    />
  );
}

const MultiCodeBlockWithoutTabs = ({
  children,
}: {
  children: React.ReactNode;
}) => <MultiCodeBlock showTabs={false}>{children}</MultiCodeBlock>;

const components = {
  wrapper: (props: StackProps) => (
    <Stack
      shouldWrapChildren
      fontSize={{ md: "lg" }}
      spacing="4"
      sx={{
        [["h1", "h2", "h3", "h4", "h5", "h6"].toString()]: {
          pt: 6,
        },
      }}
      {...props}
    />
  ),
  h2: (props: HeadingProps) => <Heading as="h2" size="xl" {...props} />,
  h3: (props: HeadingProps) => <Heading as="h3" size="lg" {...props} />,
  h4: (props: HeadingProps) => <Heading as="h4" size="md" {...props} />,
  h5: (props: HeadingProps) => <Heading as="h5" size="sm" {...props} />,
  h6: (props: HeadingProps) => <Heading as="h5" size="xs" {...props} />,
  p: (props: JSX.IntrinsicElements["p"]) => {
    return (
      <Text
        sx={{
          "img.gatsby-resp-image-image": {
            boxShadow: "none !important",
          },
        }}
      >
        <HighlightApollopediaTerms>{props.children}</HighlightApollopediaTerms>
      </Text>
    );
  },
  a: (props: LinkProps) => <CustomLink color="link" {...props} />,
  ul: (props: ListProps) => (
    <UnorderedList
      spacing="2"
      ml="10"
      sx={{
        ".chakra-collapse &": {
          marginLeft: "4",
        },
      }}
      {...props}
    />
  ),
  ol: (props: ListProps) => (
    <OrderedList
      spacing="2"
      sx={{
        ".chakra-collapse &": {
          marginLeft: "4",
        },
      }}
      {...props}
    />
  ),
  li: ({ children, ...props }: ListItemProps) => {
    return (
      <ListItem
        sx={{
          // selects all immediate children (>) of any tag (*), except for inline code
          "> *:not(code)": {
            marginBottom: "4",
          },
        }}
        {...props}
      >
        <HighlightApollopediaTerms>{children}</HighlightApollopediaTerms>
      </ListItem>
    );
  },
  inlineCode: InlineCode,
  blockquote: Blockquote,
  pre: (props: ComponentProps<typeof MarkdownCodeBlock>) => (
    <MarkdownCodeBlock isPartOfMultiCode {...props} />
  ),
  Box,
  CodeBlock,
  MultiCodeBlock,
  MultiCodeBlockWithoutTabs,
  EmbeddableExplorer,
  DownloadButton: (props: ButtonProps) => (
    <Button as="a" target="_blank" leftIcon={<FiDownload />} {...props} />
  ),
  CertificationCallout: () => null,
  RoundCornerImage,
  Screenshot,
  Cloudinary,
  CloudinaryGif,
  MultipleChoice,
  Task,
  TaskGroup,
  Editor: CodeChallenge,
  FillInTheBlanks,
  Blank,
  CollapsibleSection,
  ConditionalText,
  LessonTextContent,
  TroubleshootingSection,
  ViewCodeCollapsible,
  PrivateBlock,
  EmbedRatio,
  Disclaimer,
  DeployOnRailwayButton,
  undefined: Fragment,
  // TODO: use chakra-ui table, tr, etc.
  table: Table,
  Capsule,
  InteractiveConnectorsPlayground,
};

type LessonProps = {
  lessons: readonly Queries.LessonFragment[];
  course: {
    unlisted: boolean;
    olderVersion:
      | ({
          lessons: readonly {
            slug: string;
            tasks: readonly {
              id: string;
            }[];
          }[];
        } & Pick<
          Queries.Course,
          "id" | "title" | "slug" | "progressId" | "version"
        >)
      | null;
  } & Pick<
    Queries.Course,
    | "title"
    | "slug"
    | "marketoProgramId"
    | "marketoLeadSourceDetail"
    | "ghFeedbackEnabled"
    | "status"
    | "version"
    | "progressId"
    | "changelog"
    | "languages"
    | "thumb"
    | "educationLevel"
  >;
  frontmatter: Pick<
    Queries.MdxFrontmatter,
    "title" | "videoId" | "time" | "seo" | "description"
  >;
} & Pick<Queries.Mdx, "body" | "slug" | "timeToRead">;

export function Lesson({
  frontmatter,
  course,
  lessons,
  body,
  slug,
  timeToRead,
}: LessonProps) {
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [noPrompt, setNoPrompt] = useLocalStorage<boolean>(
    "odyssey:noprompt",
    false
  );

  const { ghFeedbackEnabled, languages } = course;
  const [language, setLanguage] = useLanguageSwitcher(
    course.slug,
    languages?.[0]
  );

  const [sidebarHidden, setSidebarHidden] = useLocalStorage(
    "odyssey:nav",
    false
  );
  const isMdOrGreater = useBreakpointValue({ base: false, md: true }, "md");
  const prevIsMd = usePrevious(isMdOrGreater);

  const [videoData, setVideoData] = useState<VideoDataProps | null>(null);

  // filter lessons arr to remove lessons that aren't relavant to the selected language
  const filteredLessons = filterLessonsByLanguage(lessons, language);

  useUpdateEffect(() => {
    // has viewport size has changed? this check is required for this to work
    // with hot reloading
    if (prevIsMd !== isMdOrGreater) {
      // automatically hide/show the sidebar depending on viewport size
      setSidebarHidden(!isMdOrGreater);
    }
  }, [isMdOrGreater, prevIsMd]);

  const headerRef = useRef<HTMLElement>(null);

  const portalNode = useMemo(
    () => (typeof window === "undefined" ? null : createHtmlPortalNode()),
    []
  );

  const { description, title, videoId, time, seo } = frontmatter || {
    title: "Untitled lesson",
  };
  const tooltipPlacement = sidebarHidden ? "right" : "bottom";

  const visibility: "hidden" | "visible" = sidebarHidden ? "hidden" : "visible";
  const fadeProps = {
    visibility,
    opacity: sidebarHidden ? 0 : 1,
    transitionProperty: "opacity",
    transitionDuration: "fast",
  };

  const lessonIndex = filteredLessons.findIndex(
    (lesson) => lesson.slug === slug
  );

  const { user, loading: isUserLoading } = useUser();
  const prompt = useCallback(
    (callback) =>
      (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        const { completeTasks } = queryTasks();

        if (
          !noPrompt &&
          !user &&
          completeTasks.length &&
          "href" in event.currentTarget
        ) {
          event.preventDefault();
          setNavigateTo(event.currentTarget.href);
          window.gtag?.("event", "prompt_shown");
        } else {
          callback(event);
        }
      },
    [noPrompt, user]
  );

  const isCourseCompleted = useMemo(
    () =>
      user?.courses
        ? user.courses.some(
            ({ id: courseId, completedAt }) =>
              completedAt && courseId === course.progressId
          )
        : false,
    [user, course.progressId]
  );

  const lessonsRemaining = useMemo(() => {
    if (!user) {
      return filteredLessons.length;
    }

    const userTasks = user.tasks ?? [];
    const completedTasks = userTasks
      .filter((task) => task.completedAt)
      .map((task) => task.id);

    if (!completedTasks.length) {
      return filteredLessons.length;
    }

    const completedLessons = filteredLessons.filter((lesson) =>
      lesson.tasks.every(({ id }) => completedTasks.includes(id))
    );

    return filteredLessons.length - completedLessons.length;
  }, [user, filteredLessons]);

  const [saveCourse, { loading: savingCourse, data: courseData }] =
    useSaveCourseMutation({
      onCompleted(data) {
        if (!user) {
          return;
        }
        if (!data?.user?.course?.completedAt) {
          return;
        }
        sendReport({
          user,
          name: course.title,
          type: "course",
          url: withPrefix("/" + data.user.course.id),
        });
      },
      update(cache, { data }) {
        if (!data?.user?.course) {
          return;
        }

        const cacheData = cache.readQuery<GetUserQuery>({
          query: GetUserDocument,
        });

        if (cacheData?.me?.__typename !== "User") {
          return;
        }

        if (
          cacheData.me.courses?.some(
            (course) => course.id === data?.user?.course?.id
          )
        ) {
          return;
        }

        const cachedCourses = cacheData.me.courses ?? [];
        cache.writeQuery<GetUserQuery>({
          query: GetUserDocument,
          data: {
            me: {
              ...cacheData.me,
              courses: [...cachedCourses, data.user.course],
            },
          },
        });
      },
    });

  const {
    isOpen: isCompletionModalOpen,
    onOpen: openCompletionModal,
    onClose: closeCompletionModal,
  } = useDisclosure();

  const handleMarketoPush = useMarketo();

  useEffect(() => {
    if (
      !lessonsRemaining &&
      !isCourseCompleted &&
      !savingCourse &&
      !courseData?.user?.course?.completedAt
    ) {
      openCompletionModal();
      const isBeta = course.status === "beta";
      window.gtag?.(
        "event",
        isBeta ? "course_complete_beta" : "course_complete",
        { course_id: course.progressId, language }
      );

      if (user) {
        // course complete event
        saveCourse({
          variables: {
            userId: user.id,
            course: {
              courseId: course.progressId,
              completedAt: new Date(),
              isBeta,
              language,
            },
          },
        });

        if (!isBeta) {
          const { marketoProgramId, marketoLeadSourceDetail } = course;
          if (marketoProgramId && marketoLeadSourceDetail) {
            // if logged in, push to the marketo program for this course with a Completion status
            handleMarketoPush({
              status: "Completion",
              programId: marketoProgramId.toString(),
              leadSourceDetail: marketoLeadSourceDetail,
              user,
            });
          }
        }
      }
    }
  }, [
    lessonsRemaining,
    isCourseCompleted,
    savingCourse,
    courseData,
    user,
    course,
    saveCourse,
    openCompletionModal,
    handleMarketoPush,
    language,
  ]);

  const scope = useMDXScope();
  const mdx = useMemo(
    () => (
      <MDXProvider components={components}>
        <MDXRenderer
          scope={{
            ...scope,
            outdent,
          }}
        >
          {body}
        </MDXRenderer>
      </MDXProvider>
    ),
    [body, scope]
  );

  const { olderVersion } = course;
  const olderVersionIsInProgress =
    user &&
    olderVersion &&
    user.courses?.some(
      (c) => !c.completedAt && c.id === olderVersion.progressId
    );
  const isOlderVersion = course.version && course.slug.includes("/");
  const noIndex = course.unlisted || isOlderVersion;
  const pageTitle = seo?.title ?? title;
  const metaDescription = seo?.description ?? description ?? undefined;

  /* Only show connectors modal if some other modal is not being shown, and user has loaded */
  const shouldShowConnectorsModal =
    !isUserLoading &&
    !isCompletionModalOpen &&
    !olderVersionIsInProgress &&
    !course.slug.includes("connectors");

  return (
    <>
      <PageSeo
        noIndex={!!noIndex}
        noFollow={!!noIndex}
        title={pageTitle}
        description={metaDescription}
        thumb={course.thumb}
      />
      <Helmet>
        {videoId && videoData && course.educationLevel && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": ["VideoObject", "LearningResource"],
              learningResourceType: "How-to",
              educationLevel: course.educationLevel,
              name: pageTitle,
              description: metaDescription,
              thumbnailUrl:
                videoData.thumbnailUrl ??
                course.thumb?.src ??
                "https://www.apollographql.com/tutorials/assets/default-course-bg.svg",
              uploadDate: new Date(videoData.uploadDate * 1000).toISOString(),
              contentUrl: `https://www.apollographql.com/tutorials/${slug}`,
            })}
          </script>
        )}
      </Helmet>
      <MultiCodeBlockContext.Provider
        value={{
          language: language ?? null,
          setLanguage,
        }}
      >
        <chakra.aside
          pos="fixed"
          top="0"
          zIndex={2}
          borderRightWidth={1}
          bg="bg"
          h="100vh"
          overflow="auto"
          w={{
            base: 320,
            md: EXPANDED_SIDEBAR_WIDTH,
          }}
          flexShrink={0}
          transform={
            sidebarHidden
              ? `translateX(calc(-100% + ${COLLAPSED_SIDEBAR_WIDTH}px))`
              : "none"
          }
          transitionProperty="transform"
          transitionDuration="normal"
        >
          <chakra.header
            px="5"
            pt="4"
            pb="3"
            pos="sticky"
            top="0"
            bg="bg"
            zIndex={1}
          >
            <Flex mb="1.5" pos="relative">
              <GatsbyLink to="/">
                <LogoTitle />
              </GatsbyLink>
              <ButtonGroup
                size="sm"
                variant="ghost"
                pos="absolute"
                top="0"
                right="-1.5"
              >
                <Stack
                  spacing={sidebarHidden ? 3 : 1.5}
                  direction={sidebarHidden ? "column-reverse" : "row"}
                >
                  <Tooltip label="Get help" placement={tooltipPlacement}>
                    <IconButton
                      aria-label="Get help"
                      icon={<FiLifeBuoy />}
                      fontSize="lg"
                      as="a"
                      href="https://community.apollographql.com/tags/c/other/22/odyssey"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        window.gtag?.("event", "discourse_link_click")
                      }
                    />
                  </Tooltip>
                  <IconButton
                    aria-label={`${sidebarHidden ? "Open" : "Close"} sidebar`}
                    fontSize="xl"
                    icon={
                      sidebarHidden ? <FiChevronsRight /> : <FiChevronsLeft />
                    }
                    onClick={() => {
                      setSidebarHidden(!sidebarHidden);
                      window.gtag?.(
                        "event",
                        `sidebar_${sidebarHidden ? "opened" : "closed"}`
                      );
                    }}
                  />
                </Stack>
              </ButtonGroup>
            </Flex>
            <Heading size="lg" title={course.title ?? undefined} {...fadeProps}>
              <Link as={GatsbyLink} to={`/${course.slug}`}>
                {course.title}
              </Link>
              {course.status && <CourseStatusLabel status={course.status} />}
            </Heading>
          </chakra.header>
          <CourseNav
            currentLesson={slug}
            lessons={filteredLessons}
            prompt={prompt}
            {...fadeProps}
          />
        </chakra.aside>
        <chakra.main
          ml={{
            base: COLLAPSED_SIDEBAR_WIDTH + "px",
            md: sidebarHidden
              ? COLLAPSED_SIDEBAR_WIDTH + "px"
              : EXPANDED_SIDEBAR_WIDTH,
          }}
          transitionProperty="margin-left"
          transitionDuration="normal"
          pos="relative"
          zIndex={0}
        >
          <Header
            ref={headerRef}
            hasBreadcrumb={false}
            afterHeader={
              isOlderVersion ? (
                <VersionBanner courseSlug={course.slug.split("/")[0]} />
              ) : undefined
            }
          >
            <chakra.span
              mr="4"
              pl="3"
              textStyle="smallCaps"
              fontSize="lg"
              isTruncated
              maxW={{
                base: "30%",
                lg: languages ? "38%" : "100%",
                xl: "100%",
              }}
            >
              {lessonIndex + 1}. {title}
            </chakra.span>
            <Flex alignItems="center" display={{ base: "none", lg: "flex" }}>
              <Box flexShrink={0} as={FiClock} />
              <chakra.span ml="1" mr="2">
                {time ?? timeToRead ?? 3}m
              </chakra.span>
            </Flex>
            {languages && languages.length > 1 && (
              <Box ml={2}>
                <LanguageSwitcher
                  activeLanguage={language}
                  languages={languages}
                  setLanguage={setLanguage}
                  eventSource="course_header"
                  courseId={course.progressId}
                />
              </Box>
            )}
          </Header>
          <Stack
            mt={{ base: 3, md: 4 }}
            maxW="container.lg"
            mx="auto"
            spacing={{ base: 6, md: 8 }}
            px={{ base: 6, md: 8 }}
            pb={{ base: 6, md: 8 }}
            sx={{
              // For any MDX components that don't render (such as ConditionalText), remove the extra spacing
              ".chakra-stack__item:empty": {
                marginTop: 0,
              },
            }}
          >
            <LineNumbersContext.Provider value={false}>
              <TaskContext.Provider
                value={{
                  courseSlug: course.progressId,
                  courseLanguage: language,
                }}
              >
                <PortalContext.Provider value={portalNode}>
                  {videoId && (
                    <WistiaEmbed
                      handleVideoData={(data) => setVideoData(data)}
                      id={videoId}
                    />
                  )}
                  {mdx}
                </PortalContext.Provider>
              </TaskContext.Provider>
            </LineNumbersContext.Provider>
            <LessonPagination
              headerRef={headerRef}
              lessonIndex={lessonIndex}
              lessons={filteredLessons}
              onComplete={openCompletionModal}
              prompt={prompt}
            />
            {ghFeedbackEnabled && (
              <>
                <Divider />

                <FeedbackLesson
                  isCourseBeta={course.status === "beta"}
                  courseTitle={course.title}
                />
              </>
            )}
          </Stack>
          {portalNode && <OutPortal node={portalNode} />}
        </chakra.main>
      </MultiCodeBlockContext.Provider>

      {user ? (
        <>
          {course.status === "beta" ? (
            <BetaCourseCompletionModal
              isOpen={isCompletionModalOpen}
              onClose={closeCompletionModal}
              title={course.title}
            />
          ) : (
            <CourseCompletionModal
              user={user}
              isOpen={isCompletionModalOpen}
              onClose={closeCompletionModal}
              title={course.title}
              courseSlug={course.slug}
              progressId={course.progressId}
            />
          )}
          <UnsavedProgress user={user} courseId={course.progressId} />
        </>
      ) : (
        <Prompt
          navigateTo={navigateTo}
          setNoPrompt={setNoPrompt}
          onClose={() => setNavigateTo(null)}
        />
      )}
      {olderVersionIsInProgress && (
        <ConfirmVersion
          user={user}
          changelog={course.changelog}
          course={olderVersion}
        />
      )}
      <ConnectorsModal user={user} isOpen={shouldShowConnectorsModal} />
    </>
  );
}
